import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export type MenuOption = { value: string; label: string };

type Props = {
  value: string;
  options: MenuOption[];
  onChange: (value: string) => void;
  className?: string;
  buttonClassName?: string;
  ariaLabel?: string;
  placeholder?: string;
};

/**
 * Settings-style dropdown (CountrySelect UX): fixed listbox, Escape/outside close.
 */
export const MenuSelect: React.FC<Props> = ({
  value,
  options,
  onChange,
  className = '',
  buttonClassName = '',
  ariaLabel,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const selected = options.find(o => o.value === value);
  const label = selected?.label || placeholder || value || '—';

  const placeMenu = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 140) });
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, placeMenu]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={
          buttonClassName ||
          `group w-full h-11 px-3 rounded-xl border text-left text-sm font-medium transition-all inline-flex items-center justify-between gap-2 ${
            open
              ? 'border-blue-500 bg-white dark:bg-slate-800 ring-2 ring-blue-500/25 text-stone-900 dark:text-white shadow-sm'
              : 'border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-900 dark:text-white hover:border-stone-300 dark:hover:border-slate-500'
          }`
        }
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200 ${open ? 'rotate-180 text-blue-500' : ''}`}
        />
      </button>
      {open && menuPos && (
        <ul
          role="listbox"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
          className="z-[80] max-h-56 overflow-y-auto rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl shadow-stone-900/15 dark:shadow-black/50 py-1 overscroll-contain"
        >
          {options.map(opt => {
            const on = opt.value === value;
            return (
              <li key={opt.value} role="option" aria-selected={on}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2 transition-colors ${
                    on
                      ? 'bg-blue-50 text-blue-800 dark:bg-blue-950/50 dark:text-blue-100'
                      : 'text-stone-700 dark:text-slate-200 hover:bg-stone-50 dark:hover:bg-slate-700/80'
                  }`}
                >
                  <span className="flex-1 min-w-0 truncate font-medium">{opt.label}</span>
                  {on ? <Check className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-300" strokeWidth={3} /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
